import { useQuery, type UseQueryOptions } from '@tanstack/react-query'

import { useDevicesApi } from '../api/context'
import { DEVICE_QUERY_KEYS } from '../api/endpoints'
import type { DeviceModelItem } from '../types/device.types'
import { toApiError } from '../utils/formatApiError'

export function useGetDeviceModels(
  options?: Omit<
    UseQueryOptions<DeviceModelItem[], Error, DeviceModelItem[]>,
    'queryKey' | 'queryFn'
  >,
) {
  const api = useDevicesApi()

  return useQuery({
    queryKey: DEVICE_QUERY_KEYS.models(),
    queryFn: async () => {
      const response = await api.getModels()
      if (!response.ok) {
        throw toApiError(response.data, 'Failed to fetch device models')
      }
      const data = response.data!
      const rawModels = (
        Array.isArray(data)
          ? data
          : ((data as { results: DeviceModelItem[] })?.results ?? [])
      ) as Record<string, unknown>[]

      return rawModels.map((model: Record<string, unknown>) => {
        const buttonsPerPage = model.buttons_per_page as number | undefined
        const pages = model.pages as number | undefined
        const dssKeyCount =
          (model.dss_key_count as number | undefined) ??
          (model.line_button_count as number | undefined) ??
          (buttonsPerPage != null && pages != null
            ? buttonsPerPage * pages
            : undefined)
        return {
          ...model,
          manufacturer_id:
            (model.manufacturer_id as number) ??
            (model.device_manufacturer_id as number) ??
            0,
          dss_key_count: dssKeyCount,
          buttons_per_page: buttonsPerPage,
          pages: pages,
          sidecar_supported:
            (model.sidecar_supported as boolean) ??
            (model.sidecar_enabled as boolean) ??
            false,
          supports_wifi:
            (model.supports_wifi as boolean) ??
            (model.supports_wifi_toggle as boolean) ??
            false,
          supports_acoustic_shield:
            (model.supports_acoustic_shield as boolean) ??
            (model.supports_acoustic_shield_toggle as boolean) ??
            false,
          manufacturer_name: model.manufacturer_name as string,
        } as DeviceModelItem
      })
    },
    ...options,
  })
}
